import { useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';
import { Heart, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui';
import { useTranslation } from '@parentpath/i18n';
import type { NameMatch } from '../types';

interface MatchNotificationProps {
  match: NameMatch;
  onClose: () => void;
  onViewMatches: () => void;
}

export function MatchNotification({
  match,
  onClose,
  onViewMatches,
}: MatchNotificationProps) {
  const { t } = useTranslation('names');
  const name = match.baby_name?.name || 'Unbekannt';
  const hasTriggeredConfetti = useRef(false);

  // Trigger confetti on mount
  useEffect(() => {
    if (hasTriggeredConfetti.current) return;
    hasTriggeredConfetti.current = true;

    // Initial burst
    const duration = 3000;
    const animationEnd = Date.now() + duration;

    // Colors matching the app theme (pink, rose, gold)
    const colors = ['#ec4899', '#f43f5e', '#fbbf24', '#a855f7', '#f472b6'];

    // Fire confetti from both sides
    const fireConfetti = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.6 },
        colors,
        zIndex: 9999,
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.6 },
        colors,
        zIndex: 9999,
      });
    };

    // Initial big burst from center
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.5, x: 0.5 },
      colors,
      zIndex: 9999,
    });

    // Continuous side confetti
    const interval = setInterval(() => {
      if (Date.now() > animationEnd) {
        clearInterval(interval);
        return;
      }
      fireConfetti();
    }, 50);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="relative bg-gradient-to-b from-white to-pink-50 rounded-3xl p-8 mx-4 max-w-sm w-full text-center animate-in zoom-in-95 duration-500 shadow-2xl border border-pink-100">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/80 flex items-center justify-center text-gray-400 hover:bg-white hover:text-gray-600 transition-colors shadow-sm"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Celebration Icon with Animation */}
        <div className="relative w-28 h-28 mx-auto mb-6">
          {/* Outer glow ring */}
          <div className="absolute inset-0 bg-gradient-to-br from-pink-400 via-rose-400 to-purple-400 rounded-full animate-pulse opacity-60 blur-md" />

          {/* Spinning outer ring */}
          <div
            className="absolute inset-0 rounded-full border-4 border-dashed border-pink-300 animate-spin"
            style={{ animationDuration: '8s' }}
          />

          {/* Main circle */}
          <div className="absolute inset-2 bg-gradient-to-br from-pink-500 via-rose-500 to-purple-500 rounded-full shadow-lg" />

          {/* Inner white circle with hearts */}
          <div className="absolute inset-4 bg-white rounded-full flex items-center justify-center shadow-inner">
            <div className="relative">
              <Heart className="w-10 h-10 text-pink-500 fill-pink-500 animate-pulse" />
              <Sparkles className="absolute -top-1 -right-1 w-4 h-4 text-yellow-400 animate-ping" />
            </div>
          </div>

          {/* Floating hearts around */}
          <Heart
            className="absolute -top-3 left-1/2 -translate-x-1/2 w-6 h-6 text-pink-400 fill-pink-400 animate-bounce"
            style={{ animationDelay: '0s', animationDuration: '1s' }}
          />
          <Heart
            className="absolute top-1/4 -left-3 w-5 h-5 text-rose-400 fill-rose-400 animate-bounce"
            style={{ animationDelay: '0.2s', animationDuration: '1.2s' }}
          />
          <Heart
            className="absolute top-1/4 -right-3 w-5 h-5 text-purple-400 fill-purple-400 animate-bounce"
            style={{ animationDelay: '0.4s', animationDuration: '1.1s' }}
          />
          <Heart
            className="absolute -bottom-2 left-1/4 w-4 h-4 text-pink-300 fill-pink-300 animate-bounce"
            style={{ animationDelay: '0.3s', animationDuration: '1.3s' }}
          />
          <Heart
            className="absolute -bottom-2 right-1/4 w-4 h-4 text-rose-300 fill-rose-300 animate-bounce"
            style={{ animationDelay: '0.5s', animationDuration: '0.9s' }}
          />
        </div>

        {/* Title with gradient */}
        <h2 className="text-3xl font-bold bg-gradient-to-r from-pink-600 via-rose-500 to-purple-600 bg-clip-text text-transparent mb-3">
          It's a Match!
        </h2>

        {/* Subtitle */}
        <p className="text-gray-500 text-sm mb-4">
          {t('match.title')}
        </p>

        {/* Name with special styling */}
        <div className="relative inline-block mb-6">
          <div className="absolute inset-0 bg-gradient-to-r from-pink-200 via-rose-200 to-purple-200 rounded-2xl blur-sm" />
          <div className="relative bg-white rounded-2xl px-8 py-4 shadow-sm border border-pink-100">
            <p className="text-4xl font-bold bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">
              {name}
            </p>
          </div>
        </div>

        {/* Description */}
        <p className="text-gray-600 mb-8 text-sm leading-relaxed">
          {t('match.description')}
        </p>

        {/* Actions */}
        <div className="space-y-3">
          <Button
            onClick={onViewMatches}
            className="w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 shadow-lg shadow-pink-200"
          >
            <Heart className="w-4 h-4 mr-2 fill-white" />
            {t('match.viewMatches')}
          </Button>
          <Button
            onClick={onClose}
            variant="secondary"
            className="w-full bg-white hover:bg-gray-50 border-2 border-gray-200"
          >
            {t('match.keepSwiping')}
          </Button>
        </div>

        {/* Bottom decoration */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-pink-400 via-rose-400 to-purple-400 rounded-b-3xl" />
      </div>
    </div>
  );
}
